import React, { useRef, useEffect } from 'react';

const CHARS = 'アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワヲン0123456789';

const MatrixRain = ({ opacity = 0.04, speed = 1, density = 0.6 }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    // Respect prefers-reduced-motion — skip animation entirely
    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (motionQuery.matches) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let animationId;
    let lastTime = 0;
    let isVisible = true;
    const interval = 1000 / 15; // ~15fps

    const fontSize = 14;
    let columns = 0;
    let drops = [];

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      const newColumns = Math.floor(canvas.width / fontSize);
      if (newColumns !== columns) {
        const newDrops = new Array(newColumns).fill(0).map((_, i) =>
          i < columns ? drops[i] : Math.random() * -100
        );
        columns = newColumns;
        drops = newDrops;
      }
    };

    resize();
    window.addEventListener('resize', resize);

    // Pause animation when canvas is off-screen
    const observer = new IntersectionObserver(
      ([entry]) => {
        isVisible = entry.isIntersecting;
        if (isVisible && !animationId) {
          animationId = requestAnimationFrame(draw);
        }
      },
      { threshold: 0 }
    );
    observer.observe(canvas);

    const draw = (timestamp) => {
      if (!isVisible) {
        animationId = null;
        return;
      }
      animationId = requestAnimationFrame(draw);

      if (timestamp - lastTime < interval) return;
      lastTime = timestamp;

      ctx.fillStyle = `rgba(var(--color-surface), 0.12)`;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.fillStyle = `rgba(var(--color-green), ${opacity})`;
      ctx.font = `${fontSize}px monospace`;

      for (let i = 0; i < columns; i++) {
        if (Math.random() > density) continue;

        const char = CHARS[Math.floor(Math.random() * CHARS.length)];
        const x = i * fontSize;
        const y = drops[i] * fontSize;

        ctx.fillText(char, x, y);

        if (y > canvas.height && Math.random() > 0.975) {
          drops[i] = 0;
        }
        drops[i] += speed;
      }
    };

    animationId = requestAnimationFrame(draw);

    return () => {
      if (animationId) cancelAnimationFrame(animationId);
      observer.disconnect();
      window.removeEventListener('resize', resize);
    };
  }, [opacity, speed, density]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="absolute inset-0 w-full h-full pointer-events-none"
    />
  );
};

export default MatrixRain;
